import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Loader2, ImageOff, ChevronLeft, ChevronRight, MessageCircle, ShieldCheck, CheckCircle2, MapPin } from 'lucide-react';
import NotFound from './NotFound';

const Blog = ({ language }) => { 
  const navigate = useNavigate();
  const { slug } = useParams();

  const [posts, setPosts] = useState([]);
  const [singlePost, setSinglePost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const t = {
    es: {
      tag: "Nuestra Actualidad",
      title: "Blog Corporativo",
      subtitle: "Estrategias, actualización legal y tranquilidad operativa para el dueño de empresa en Colombia.",
      loading: "Cargando contenido...",
      errorMsg: "Hubo un error cargando el contenido. Intenta más tarde.",
      blogBadge: "NOTICIA",
      defaultAuthor: "Equipo Zaphy",
      readMore: "Leer artículo completo",
      noPosts: "No hay noticias publicadas todavía. ¡Vuelve pronto!",
      prev: "Anterior",
      next: "Siguiente",
      page: "Página",
      backToBlog: "Volver a Noticias",
      publishedOn: "Publicado el",
      by: "Por",
      ctaTitle: "¿Tu empresa está realmente protegida?",
      ctaSubtitle: "Evita sanciones y multas del Ministerio. Asumimos tu departamento de SG-SST y Gestión Humana con total respaldo.",
      ctaBtn: "Hablar con un Especialista",
      whatsappMsg: "Hola Zaphy, leí su artículo en el blog y quiero proteger mi empresa."
    },
    en: {
      tag: "Our Latest News",
      title: "Corporate Blog",
      subtitle: "Strategies, legal updates, and operational peace of mind for business owners.",
      loading: "Loading content...",
      errorMsg: "There was an error loading. Please try again later.",
      blogBadge: "NEWS",
      defaultAuthor: "Zaphy Team",
      readMore: "Read full article",
      noPosts: "No news published yet. Come back soon!",
      prev: "Previous",
      next: "Next",
      page: "Page",
      backToBlog: "Back to News",
      publishedOn: "Published on",
      by: "By",
      ctaTitle: "Is your company fully protected?",
      ctaSubtitle: "Avoid Ministry fines. We manage your HR & Work Safety so you can focus on growth.",
      ctaBtn: "Talk to a Specialist",
      whatsappMsg: "Hello Zaphy, I read your blog article and I want to protect my company."
    }
  };

  const text = t[language] || t.es;

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      setError(null);
      
      const siteUrl = window.zaphyData?.siteUrl || 'https://zaphy.com.co'; 

      try {
        if (slug) {
          const response = await fetch(`${siteUrl}/wp-json/wp/v2/posts?slug=${slug}&_embed`);
          const data = await response.json();
          
          if (data && data.length > 0) {
            setSinglePost(data[0]);
            document.title = `${data[0].title.rendered} - Zaphy Gestión Integral`;
          } else {
            setSinglePost(null);
            setError('not_found');
          }
        } else {
          setSinglePost(null);
          const response = await fetch(`${siteUrl}/wp-json/wp/v2/posts?_embed&per_page=9&page=${currentPage}`);
          
          if (!response.ok) throw new Error('Error de red');

          const totalPagesHeader = response.headers.get('X-WP-TotalPages');
          if (totalPagesHeader) {
            setTotalPages(parseInt(totalPagesHeader, 10));
          }

          const data = await response.json();
          setPosts(data);
          document.title = `Blog Corporativo - Zaphy Gestión Integral`;
        }
      } catch (err) {
        console.error("Error fetching content:", err);
        setError('network'); 
      } finally {
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    fetchContent();
  }, [slug, currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleReadPost = (postSlug) => {
    navigate(`/${postSlug}`);
  };

  const handleBackToList = () => {
    navigate('/blog');
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    const locale = language === 'en' ? 'en-US' : 'es-ES';
    return new Date(dateString).toLocaleDateString(locale, options);
  };

  // --- VISTA 1: ARTÍCULO INDIVIDUAL OPTIMIZADO ---
  if (slug && !loading) {
    if (error === 'not_found' || !singlePost) {
      return <NotFound language={language} />; 
    }

    const featuredMedia = singlePost._embedded?.['wp:featuredmedia']?.[0]?.source_url;
    const authorName = singlePost._embedded?.author?.[0]?.name || text.defaultAuthor;

    return (
      <div className="pt-24 pb-24 bg-slate-50/50 min-h-screen animate-[fadeIn_0.5s_ease-out]">
        
        {/* HEADER DEL ARTÍCULO */}
        <div className="relative h-[400px] md:h-[480px] bg-slate-900 text-white overflow-hidden">
          {featuredMedia ? (
            <>
              <img 
                src={featuredMedia} 
                alt={singlePost.title.rendered} 
                className="absolute inset-0 w-full h-full object-cover opacity-35 blur-[1px] scale-105" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/75 to-transparent"></div>
            </>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-purple-950 via-slate-900 to-indigo-950"></div>
          )}
          
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-12 relative z-10">
            <button 
              onClick={handleBackToList}
              className="absolute top-8 left-4 md:left-6 flex items-center gap-2 text-white/90 hover:text-white bg-black/40 hover:bg-black/60 backdrop-blur-md px-5 py-2.5 rounded-full text-sm font-semibold transition-all border border-white/10 shadow-lg"
            >
              <ArrowLeft size={18}/> {text.backToBlog}
            </button>
            
            <div className="flex items-center gap-3 text-xs md:text-sm text-purple-300 mb-4 font-semibold tracking-wide">
              <span className="bg-purple-600/90 backdrop-blur-md px-3.5 py-1 rounded-full text-white uppercase text-xs font-bold tracking-wider shadow-sm">
                {text.blogBadge}
              </span>
              <span>•</span>
              <span className="flex items-center gap-1.5"><Calendar size={15}/> {formatDate(singlePost.date)}</span>
            </div>
            
            <h1 
              className="text-3xl sm:text-4xl md:text-5xl font-black leading-[1.15] mb-6 text-white font-[Poppins] max-w-4xl drop-shadow-md"
              dangerouslySetInnerHTML={{ __html: singlePost.title.rendered }}
            />
            
            <div className="flex items-center gap-3 text-slate-300 font-medium text-sm md:text-base">
              <div className="w-9 h-9 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold text-sm shadow-md">
                {authorName.charAt(0)}
              </div>
              <span>{text.by} <strong className="text-white font-semibold">{authorName}</strong></span>
            </div>
          </div>
        </div>

        {/* ESTRUCTURA PRINCIPAL DEL ARTÍCULO */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20">
          
          {/* ESTILOS CSS INYECTADOS: JUSTIFICACIÓN + REGLAS TIPOGRÁFICAS */}
          <style>{`
            .zaphy-content {
              font-family: 'Poppins', sans-serif;
              color: #334155;
              line-height: 1.85;
              font-size: 1.125rem;
            }
            .zaphy-content p {
              margin-bottom: 1.5rem !important;
              text-align: justify !important;
              text-justify: inter-word;
            }
            .zaphy-content h2 {
              font-size: 1.85rem !important;
              font-weight: 800 !important;
              color: #0F172A !important;
              margin-top: 2.5rem !important;
              margin-bottom: 1.25rem !important;
              line-height: 1.3 !important;
              border-left: 4px solid #9333EA;
              padding-left: 1rem;
              clear: both;
            }
            .zaphy-content h3 {
              font-size: 1.4rem !important;
              font-weight: 700 !important;
              color: #1E293B !important;
              margin-top: 2rem !important;
              margin-bottom: 1rem !important;
              line-height: 1.35 !important;
              clear: both;
            }
            .zaphy-content ul, .zaphy-content ol {
              margin-bottom: 1.75rem !important;
              padding-left: 1.5rem !important;
              text-align: justify !important;
            }
            .zaphy-content li {
              margin-bottom: 0.5rem !important;
            }
            .zaphy-content blockquote {
              background-color: #F8FAFC;
              border-left: 4px solid #06B6D4;
              padding: 1.25rem 1.5rem;
              margin: 2rem 0;
              border-radius: 0 1rem 1rem 0;
              font-style: italic;
              color: #475569;
              font-weight: 500;
              text-align: justify !important;
            }
            .zaphy-content table {
              width: 100% !important;
              border-collapse: separate !important;
              border-spacing: 0 !important;
              margin: 2rem 0 !important;
              border-radius: 1rem !important;
              overflow: hidden !important;
              border: 1px solid #E2E8F0 !important;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
            }
            .zaphy-content th {
              background-color: #0F172A !important;
              color: #ffffff !important;
              padding: 1rem !important;
              text-align: left !important;
              font-weight: 700 !important;
              font-size: 0.95rem !important;
            }
            .zaphy-content td {
              padding: 1rem !important;
              border-bottom: 1px solid #E2E8F0 !important;
              font-size: 0.95rem !important;
              background-color: #ffffff;
            }
            .zaphy-content tr:nth-child(even) td {
              background-color: #F8FAFC !important;
            }
            .zaphy-content img {
              border-radius: 1.25rem !important;
              margin: 2rem auto !important;
              box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1) !important;
              max-width: 100% !important;
              height: auto !important;
              display: block;
            }
          `}</style>

          {/* GRID INTELIGENTE: El sidebar flota fixed/sticky al inicio y luego el contenido ocupa todo el ancho */}
          <div className="relative">
            
            {/* SIDEBAR FLOTANTE FIX / STICKY (Estilo Alex Hormozi) */}
            <aside className="lg:float-right lg:w-80 lg:ml-8 mb-8 lg:mb-4 lg:sticky lg:top-28 z-30">
              <div className="bg-white p-7 rounded-3xl shadow-xl border border-purple-100/80 text-center relative overflow-hidden">
                <div className="absolute -top-12 -right-12 w-24 h-24 bg-purple-50 rounded-full blur-xl pointer-events-none"></div>
                
                <div className="w-14 h-14 bg-purple-100 text-purple-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner">
                  <ShieldCheck size={32} />
                </div>
                
                <span className="inline-block bg-purple-100 text-purple-800 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider mb-3">
                  Tu Aliado Estratégico
                </span>

                <h3 className="text-xl font-black text-slate-900 mb-2 font-[Poppins] leading-tight">
                  Tu Dpto. de RRHH y SST Externo
                </h3>
                
                <p className="text-slate-600 text-xs mb-5 leading-relaxed font-[Poppins] text-center">
                  Cuesta menos que un empleado sancionado. Protegemos tu empresa en <strong className="text-slate-800">Bogotá, Medellín y Cali</strong> para que operes 100% tranquilo.
                </p>
                
                <ul className="text-left text-xs text-slate-700 space-y-2.5 mb-6 font-[Poppins]">
                  <li className="flex items-center gap-2 font-medium">
                    <CheckCircle2 size={15} className="text-purple-600 shrink-0"/> Blindaje total contra inspecciones
                  </li>
                  <li className="flex items-center gap-2 font-medium">
                    <CheckCircle2 size={15} className="text-purple-600 shrink-0"/> Nómina exacta y sin errores
                  </li>
                  <li className="flex items-center gap-2 font-medium">
                    <CheckCircle2 size={15} className="text-purple-600 shrink-0"/> Acompañamiento legal continuo
                  </li>
                </ul>

                <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400 font-semibold mb-5">
                  <MapPin size={13} className="text-purple-500" />
                  <span>Bogotá • Medellín • Cali</span>
                </div>

                <a 
                  href="https://wa.me/573114044203?text=Hola%20Zaphy,%20quiero%20un%20diagn%C3%B3stico%20sin%20costo%20para%20mi%20empresa."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center bg-purple-700 hover:bg-purple-800 text-white font-bold py-3.5 px-4 rounded-xl shadow-lg hover:shadow-purple-500/25 transition-all text-xs font-[Poppins] uppercase tracking-wider"
                >
                  Blindar mi Empresa Ahora
                </a>
              </div>
            </aside>

            {/* CONTENIDO PRINCIPAL DEL BLOG */}
            <main className="bg-white p-6 md:p-12 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100">
              
              <div 
                className="zaphy-content"
                dangerouslySetInnerHTML={{ __html: singlePost.content.rendered }}
              />

              {/* LIMPIEZA DE FLOTANTE CSS */}
              <div className="clear-both"></div>

              {/* BANNER FINAL DE CONVERSIÓN EN EL ARTÍCULO */}
              <div className="mt-12 p-8 rounded-2xl bg-gradient-to-r from-purple-950 via-slate-900 to-indigo-950 text-white shadow-xl relative overflow-hidden">
                <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                  <div>
                    <h4 className="text-2xl font-black mb-2">{text.ctaTitle}</h4>
                    <p className="text-purple-200 text-sm max-w-md leading-relaxed">{text.ctaSubtitle}</p>
                  </div>
                  <a 
                    href={`https://wa.me/573114044203?text=${encodeURIComponent(text.whatsappMsg)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-3.5 rounded-full shadow-lg hover:shadow-green-500/30 transition-all text-sm whitespace-nowrap uppercase tracking-wider"
                  >
                    <MessageCircle size={20} /> {text.ctaBtn}
                  </a>
                </div>
              </div>

              <div className="mt-10 pt-6 border-t border-slate-100 flex justify-between items-center">
                <button 
                  onClick={handleBackToList}
                  className="text-purple-700 font-bold hover:text-purple-900 flex items-center gap-2 transition-colors text-sm"
                >
                  <ArrowLeft size={18}/> {text.backToBlog}
                </button>
              </div>

            </main>

          </div>
        </div>
      </div>
    );
  }

  // --- VISTA 2: LISTA DE ARTÍCULOS (GRID COMPLETO E INTACTO) ---
  return (
    <div className="pt-32 pb-20 bg-gray-50 min-h-screen animate-[fadeIn_0.5s_ease-out]">
       <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
         
         {!loading && (
             <div className="text-center mb-16">
                <span className="text-purple-600 font-bold tracking-[0.2em] uppercase text-xs mb-3 block font-[Poppins]">{text.tag}</span>
                <h2 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-6 font-[Poppins]">
                  {text.title}
                </h2>
                <p className="text-xl text-gray-500 max-w-2xl mx-auto font-[Poppins]">
                  {text.subtitle}
                </p>
             </div>
         )}

         {loading && (
           <div className="flex flex-col items-center justify-center h-64 text-purple-600">
              <Loader2 className="animate-spin mb-4" size={48} />
              <p className="font-[Poppins]">{text.loading}</p>
           </div>
         )}

         {error && !loading && (
           <div className="text-center py-20">
              <p className="text-red-500 font-bold text-lg font-[Poppins]">{text.errorMsg}</p>
           </div>
         )}

         {!loading && !error && (
           <>
             <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
               {posts.map((post) => {
                 const featuredMedia = post._embedded?.['wp:featuredmedia']?.[0]?.source_url;
                 const authorName = post._embedded?.author?.[0]?.name || text.defaultAuthor;

                 return (
                   <article 
                      key={post.id} 
                      onClick={() => handleReadPost(post.slug)}
                      className="bg-white rounded-[30px] overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group border border-gray-100 flex flex-col h-full transform hover:-translate-y-2 cursor-pointer"
                   >
                       <div className="h-56 relative overflow-hidden bg-gray-200">
                          <div className="absolute top-4 right-4 bg-purple-900 text-white text-[10px] font-bold px-3 py-1 rounded-full tracking-wider z-10 font-[Poppins]">
                             {text.blogBadge}
                          </div>
                          
                          {featuredMedia ? (
                            <img 
                              src={featuredMedia} 
                              alt={post.title.rendered} 
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-100">
                               <ImageOff size={48} />
                            </div>
                          )}
                          
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                       </div>

                       <div className="p-8 flex-1 flex flex-col relative">
                          <div className="flex items-center gap-4 text-xs text-gray-400 mb-4 font-[Poppins]">
                             <div className="flex items-center gap-1">
                                <Calendar size={14} className="text-purple-500"/>
                                {formatDate(post.date)}
                             </div>
                             <div className="flex items-center gap-1">
                                <User size={14} className="text-cyan-500"/>
                                {authorName}
                             </div>
                          </div>

                          <h3 
                            className="text-xl font-bold text-gray-900 leading-tight group-hover:text-purple-700 transition-colors mb-4 font-[Poppins] line-clamp-2"
                            dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                          />
                          
                          <div 
                            className="text-gray-500 text-sm leading-relaxed mb-6 font-[Poppins] line-clamp-3 flex-grow"
                            dangerouslySetInnerHTML={{ __html: post.excerpt.rendered }}
                          />

                          <div className="mt-auto inline-flex items-center gap-2 text-purple-600 font-bold text-sm hover:text-purple-800 transition-colors group/btn font-[Poppins]">
                            {text.readMore} 
                            <ArrowLeft size={16} className="transition-transform group-hover/btn:-translate-x-1 rotate-180"/>
                          </div>
                       </div>
                   </article>
                 );
               })}
             </div>

             {totalPages > 1 && (
               <div className="flex justify-center items-center gap-4 py-8 border-t border-gray-100">
                  <button 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all ${
                      currentPage === 1 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : 'bg-white text-purple-700 border-2 border-purple-100 hover:bg-purple-50 hover:border-purple-300 shadow-sm'
                    }`}
                  >
                     <ChevronLeft size={20} /> {text.prev}
                  </button>

                  <span className="font-[Poppins] text-sm font-medium text-gray-500">
                      {text.page} <span className="text-purple-700 font-bold">{currentPage}</span> / {totalPages}
                  </span>

                  <button 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all ${
                      currentPage === totalPages 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                        : 'bg-white text-purple-700 border-2 border-purple-100 hover:bg-purple-50 hover:border-purple-300 shadow-sm'
                    }`}
                  >
                     {text.next} <ChevronRight size={20} />
                  </button>
               </div>
             )}
           </>
         )}

         {!loading && posts.length === 0 && !error && (
            <div className="text-center py-20 text-gray-500 font-[Poppins]">
               {text.noPosts}
            </div>
         )}

       </div>
    </div>
  );
};

export default Blog;